import { useCallback } from 'react';
import { ReactComponent as NoCurrencyExchangeIcon } from '../../assets/icons/no_exchange.svg';
import { ReactComponent as NoCurrencyExchangeHighRateIcon } from '../../assets/icons/no_exchange_high_exchange_rate.svg';
import { ReactComponent as NoCurrencyExchangeTimeIcon } from '../../assets/icons/no_exchange_time.svg';
import { ReactComponent as ErrorIcon } from '../../assets/svg/error_icon.svg';
import { useDialog } from '../../context/DialogContext';
import { useLocale } from '../../context/NbLocalizationContext';
import { useTheme } from '../../context/NbThemeContext';
import classes from '../../modules/core/components/cash-popover/CashPopoverContent.module.scss';
import { CurrencyExchangeNotAvailableModal } from '../../modules/core/components/currency-exchange/CurrencyExchangeNotAvailableModal';
import { Config } from '../config/config';

export const useCurrencyExchangeModals = () => {
  const { translation, translateWithParams } = useLocale();
  const { openDialog } = useDialog();
  const { colors } = useTheme();

  const openCurrentlyNoExchangeDialog = useCallback(async () => {
    await openDialog((close: () => void) => (
      <CurrencyExchangeNotAvailableModal
        icon={<NoCurrencyExchangeIcon />}
        title={translation.currencyChange.note.currentlyNoExchange}
        text={translation.currencyChange.note.tryLater}
        onCancel={close}
      />
    ));
  }, [translation, openDialog]);

  const openNoExchangePeriodDialog = useCallback(
    async (currency?: string) => {
      const nextExchangeTime =
        !currency || (currency && Config.NetBroker.CurrencyExchange.HaveNightPeriods.includes(currency))
          ? translation.currencyChange.note.withNightExchangePeriod
          : translation.currencyChange.note.nonNightExchangePeriod;

      await openDialog((close: () => void) => (
        <CurrencyExchangeNotAvailableModal
          icon={<NoCurrencyExchangeTimeIcon style={{ color: colors.primary }} />}
          title={
            currency
              ? translateWithParams(translation.currencyChange.note.noExchangePeriodForCurrency, { currency })
              : translation.currencyChange.note.noExchangePeriod
          }
          text={translation.currencyChange.note.tryLaterFixTime}
          onCancel={close}
        >
          <p className={classes.noExchangeTime}>{nextExchangeTime}</p>
        </CurrencyExchangeNotAvailableModal>
      ));
    },
    [translation, translateWithParams, openDialog, colors.primary],
  );

  const openGlobalLimitExceedDialog = useCallback(
    async (currency: string) => {
      const title = translateWithParams(translation.currencyChange.note.globalLimitExceeded, { currency });
      await openDialog((close: () => void) => (
        <CurrencyExchangeNotAvailableModal
          icon={<NoCurrencyExchangeHighRateIcon />}
          title={title}
          text={translation.currencyChange.note.tryLaterWithoutLimit}
          onCancel={close}
        >
          <p className={classes.noExchangeTime}>{translation.currencyChange.note.limitFreeExchangePeriod}</p>
        </CurrencyExchangeNotAvailableModal>
      ));
    },
    [translation, openDialog, translateWithParams],
  );

  const openExchangeRateDiffDialog = useCallback(
    async (currency: string) => {
      const title = translateWithParams(translation.currencyChange.note.highExchange, { currency });
      await openDialog((close: () => void) => (
        <CurrencyExchangeNotAvailableModal
          icon={<NoCurrencyExchangeHighRateIcon />}
          title={title}
          text={translation.currencyChange.note.tryLaterWithoutLimit}
          onCancel={close}
        >
          <p className={classes.noExchangeTime}>{translation.currencyChange.note.limitFreeExchangePeriod}</p>
        </CurrencyExchangeNotAvailableModal>
      ));
    },
    [translation, openDialog, translateWithParams],
  );

  const openErrorDialog = useCallback(async () => {
    await openDialog((close: () => void) => (
      <CurrencyExchangeNotAvailableModal
        icon={<ErrorIcon />}
        title={translation.currencyChange.note.error}
        text={translation.currencyChange.note.tryLater}
        onCancel={close}
      ></CurrencyExchangeNotAvailableModal>
    ));
  }, [translation, openDialog]);

  return {
    openCurrentlyNoExchangeDialog,
    openNoExchangePeriodDialog,
    openGlobalLimitExceedDialog,
    openExchangeRateDiffDialog,
    openErrorDialog,
  };
};
